import { Html, TransformControls } from "@react-three/drei";
import { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { usePlacedModelsStore, useUserInterfaceStore } from "../../store";
import TransformToolsCircular from "../ObjectPanel/TransformToolsCircular";
import { ScaleControls } from "./ScaleControl";
import useIsMobile from "../Hooks/useIsMobile";

// Must match the scale factor in GLBObject.js
const ASSET_SCALE_FACTOR = 0.85;

function CustomTransformControls({ selectedMeshes }) {
  const snappingForEditor = useUserInterfaceStore((s) => s.snappingForEditor);
  const setIsTransforming = useUserInterfaceStore((s) => s.setIsTransforming);
  const rotationAxes = useUserInterfaceStore((s) => s.rotationAxes);
  const isMobile = useIsMobile();

  const gizmoRef = useRef(new THREE.Object3D());

  const prevPosition = useRef(new THREE.Vector3());
  const prevQuat = useRef(new THREE.Quaternion());
  const prevScale = useRef(new THREE.Vector3(1, 1, 1));

  // 👇 local state to track which tool is active: "translate" | "rotate" | "scale" | null
  const [activeTool, setActiveTool] = useState(null);

  const translateControlsRef = useRef(null);

  const updateTransformCallback = useCallback(() => {
    const updateTransform = usePlacedModelsStore.getState().updateTransform;

    selectedMeshes.forEach((mesh) => {
      if (mesh.userData?.isGroupRoot) {
        const id = mesh.userData.sourceId;
        // The mesh is rendered with scale * ASSET_SCALE_FACTOR, so we need to
        // divide by ASSET_SCALE_FACTOR to store the original unscaled value
        const visualScale = mesh.scale.toArray();
        const originalScale = visualScale.map(s => s / ASSET_SCALE_FACTOR);
        const transform = {
          position: mesh.position.toArray(),
          rotation: [mesh.rotation.x, mesh.rotation.y, mesh.rotation.z],
          scale: originalScale,
        };
        updateTransform(id, transform);
      }
    });

    // reset active tool after finishing
    setActiveTool(null);
    // delay clearing isTransforming so the pointer-up event cycle completes first
    setTimeout(() => setIsTransforming(false), 100);
  }, [selectedMeshes, setIsTransforming]);

  useEffect(() => {
    if (!selectedMeshes || selectedMeshes.length === 0) return;

    const center = new THREE.Vector3();
    const temp = new THREE.Vector3();

    selectedMeshes.forEach((mesh) => {
      mesh.getWorldPosition(temp);
      center.add(temp);
    });

    center.divideScalar(selectedMeshes.length);

    gizmoRef.current.position.copy(center);
    gizmoRef.current.rotation.set(0, 0, 0);
    gizmoRef.current.scale.set(1, 1, 1);

    prevPosition.current.copy(center);
    prevQuat.current.copy(gizmoRef.current.quaternion);
    prevScale.current.copy(gizmoRef.current.scale);
  }, [selectedMeshes]);

  // Remove the center XYZ omni-handle (rhombus) from the translate gizmo.
  // We must remove the children entirely because updateMatrixWorld resets
  // handle.visible = true on every frame.
  useEffect(() => {
    const controls = translateControlsRef.current;
    if (!controls) return;
    // The TransformControlsGizmo is the first child of TransformControls
    const gizmoRoot = controls.children.find(c => c.isTransformControlsGizmo);
    if (!gizmoRoot) return;
    // gizmoRoot.gizmo["translate"] and gizmoRoot.picker["translate"] are groups
    // whose children have names like X, Y, Z, XY, XZ, YZ, XYZ
    const removedChildren = [];
    [gizmoRoot.gizmo?.["translate"], gizmoRoot.picker?.["translate"]].forEach(group => {
      if (!group) return;
      const toRemove = group.children.filter(c => c.name === 'XYZ');
      toRemove.forEach(child => {
        group.remove(child);
        removedChildren.push({ group, child });
      });
    });
    return () => {
      // Restore on cleanup so remounting works correctly
      removedChildren.forEach(({ group, child }) => group.add(child));
    };
  }, [activeTool, selectedMeshes]);

  const handleTransformChange = useCallback(() => {
    const gizmo = gizmoRef.current;

    if (!gizmo || selectedMeshes.length === 0) return;

    const centerPos = gizmo.position.clone();

    // Compute deltas
    const deltaPos = new THREE.Vector3().subVectors(
      gizmo.position,
      prevPosition.current
    );

    const deltaQuat = gizmo.quaternion
      .clone()
      .multiply(prevQuat.current.clone().invert());

    const deltaScale = gizmo.scale.clone().divide(prevScale.current);

    selectedMeshes.forEach((mesh) => {
      // 🛠 Ensure parent world matrix is updated
      if (mesh.parent) {
        mesh.parent.updateMatrixWorld(true);
      }

      // --- POSITION (translate around center) ---
      const originalWorldPos = new THREE.Vector3();
      mesh.getWorldPosition(originalWorldPos);

      const offset = originalWorldPos.clone().sub(centerPos);

      // Apply rotation and scale to offset
      offset.applyQuaternion(deltaQuat);
      offset.multiply(deltaScale);

      const newWorldPos = centerPos.clone().add(offset).add(deltaPos);

      if (mesh.parent) {
        mesh.parent.worldToLocal(newWorldPos);
      }
      mesh.position.copy(newWorldPos);

      // --- ROTATION ---
      const worldQuat = new THREE.Quaternion();
      mesh.getWorldQuaternion(worldQuat);
      worldQuat.premultiply(deltaQuat);

      const parentQuat = new THREE.Quaternion();
      mesh.parent?.getWorldQuaternion(parentQuat);
      parentQuat.invert();
      mesh.quaternion.copy(worldQuat.premultiply(parentQuat));
      // --- SCALE (with orientation and center awareness) ---
      // Save original position and quaternion
      const originalPosition = mesh.position.clone();
      const originalQuaternion = mesh.quaternion.clone();

      // Apply scaling matrix around gizmo center
      const gizmoCenter = gizmo.position.clone();
      const gizmoScale = deltaScale.clone(); // Vector3 scale multiplier

      const scaleMatrix = new THREE.Matrix4().makeScale(
        gizmoScale.x,
        gizmoScale.x,
        gizmoScale.x
      );
      const tToOrigin = new THREE.Matrix4().makeTranslation(
        -gizmoCenter.x,
        -gizmoCenter.y,
        -gizmoCenter.z
      );
      const tBack = new THREE.Matrix4().makeTranslation(
        gizmoCenter.x,
        gizmoCenter.y,
        gizmoCenter.z
      );
      const centerScaleMatrix = new THREE.Matrix4()
        .multiply(tBack)
        .multiply(scaleMatrix)
        .multiply(tToOrigin);

      // Apply to world matrix
      const meshWorldMatrix = mesh.matrixWorld.clone();
      meshWorldMatrix.premultiply(centerScaleMatrix);

      // Convert to local space
      const parentInverse = new THREE.Matrix4()
        .copy(mesh.parent.matrixWorld)
        .invert();
      const meshLocalMatrix = meshWorldMatrix.premultiply(parentInverse);

      // Decompose
      const tempPos = new THREE.Vector3();
      const tempQuat = new THREE.Quaternion();
      const tempScale = new THREE.Vector3();
      meshLocalMatrix.decompose(tempPos, tempQuat, tempScale);

      // Apply only scale
      mesh.scale.copy(tempScale);

      // Restore position and rotation
      mesh.position.copy(originalPosition);
      mesh.quaternion.copy(originalQuaternion);

      // Final update
      mesh.updateMatrix();
      mesh.updateMatrixWorld(true);
    });

    // 🔄 Store previous transform for next delta
    prevPosition.current.copy(gizmo.position);
    prevQuat.current.copy(gizmo.quaternion);
    prevScale.current.copy(gizmo.scale);
  }, [selectedMeshes]);

  return (
    <>
      <primitive object={gizmoRef.current} />
      {selectedMeshes?.length > 0 && (
        <>
          {!isMobile && (activeTool === null || activeTool === "translate") && (
            <TransformControls
              ref={translateControlsRef}
              space="world"
              object={gizmoRef.current}
              mode="translate"
              size={1.5}
              translationSnap={snappingForEditor ? 2.5 : 0.001}
              onMouseDown={() => { setActiveTool("translate"); setIsTransforming(true); }}
              onMouseUp={updateTransformCallback}
              onObjectChange={handleTransformChange}
              visible={activeTool === null || activeTool === "translate"}
            />
          )}
          {!isMobile && (activeTool === null || activeTool === "rotate") && (
            <TransformControls
              space="world"
              object={gizmoRef.current}
              mode="rotate"
              showX={rotationAxes.x}
              showY={rotationAxes.y}
              showZ={rotationAxes.z}
              size={2}
              scaleSnap={snappingForEditor ? 0.5 : 0.001}
              translationSnap={snappingForEditor ? 5 : 0.001}
              rotationSnap={snappingForEditor ? Math.PI / 8 : 0.001}
              onMouseDown={() => { setActiveTool("rotate"); setIsTransforming(true); }}
              onMouseUp={updateTransformCallback}
              onObjectChange={handleTransformChange}
              visible={activeTool === null || activeTool === "rotate"}
            />
          )}
          {!isMobile && (activeTool === null || activeTool === "scale") && (
            <ScaleControls
              object={gizmoRef.current}
              gizmoRef={gizmoRef}
              visible={activeTool === null || activeTool === "scale"}
              scaleSnap={snappingForEditor ? 0.1 : 0.001}
              onMouseDown={() => { setActiveTool("scale"); setIsTransforming(true); }}
              onMouseUp={updateTransformCallback}
              onObjectChange={handleTransformChange}
            />
          )}

          <SelectionUI selectedMeshes={selectedMeshes} />
        </>
      )}
    </>
  );
}

export default CustomTransformControls;

function SelectionUI({ selectedMeshes }) {
  if (!selectedMeshes) return null;

  const worldPos = selectedMeshes[0].getWorldPosition(new THREE.Vector3());

  return (
    <Html position={worldPos} center>
      <TransformToolsCircular />
    </Html>
  );
}
